import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { navForRole } from "./navConfig";
import LanguageToggle from "./LanguageToggle";

export default function MobileHeader() {
    const { user, logout } = useAuth();
    const { t } = useI18n();
    const [open, setOpen] = useState(false);
    if (!user) return null;
    const groups = navForRole(user.role);

    const initials = (user.full_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

    return (
        <>
            <header
                className="md:hidden sticky top-0 z-40 h-16 bg-card border-b border-border flex items-center justify-between px-4"
                data-testid="mobile-header"
            >
                <div className="avatar-ring h-9 w-9">
                    <div className="h-full w-full flex items-center justify-center text-xs font-semibold text-primary">
                        {initials}
                    </div>
                </div>
                <span className="text-sm font-semibold">{t("app.name")}</span>
                <button
                    onClick={() => setOpen(true)}
                    className="p-2 rounded-lg hover:bg-secondary"
                    aria-label="Open menu"
                    data-testid="mobile-menu-button"
                >
                    <Menu className="h-5 w-5" />
                </button>
            </header>

            {open && (
                <div className="md:hidden fixed inset-0 z-50 bg-card flex flex-col" data-testid="mobile-drawer">
                    <div className="h-16 flex items-center justify-between px-4 border-b border-border">
                        <span className="text-sm font-semibold">{t("app.name")}</span>
                        <button
                            onClick={() => setOpen(false)}
                            className="p-2 rounded-lg hover:bg-secondary"
                            aria-label="Close menu"
                            data-testid="mobile-menu-close"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                    <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
                        {groups.map((group) => (
                            <div key={group.label}>
                                <p className="px-3 mb-2 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                                    {t(group.label)}
                                </p>
                                <div className="space-y-1">
                                    {group.items.map(({ to, labelKey, Icon, testId }) => (
                                        <NavLink
                                            key={to}
                                            to={to}
                                            onClick={() => setOpen(false)}
                                            className={({ isActive }) => `nav-row ${isActive ? "active" : ""}`}
                                            data-testid={`${testId}-mobile`}
                                        >
                                            <Icon className="h-5 w-5 shrink-0" />
                                            <span className="truncate">{t(labelKey)}</span>
                                        </NavLink>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </nav>
                    <div className="border-t border-border p-4 space-y-3">
                        <LanguageToggle />
                        <button
                            onClick={() => { setOpen(false); logout(); }}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-secondary text-secondary-foreground text-sm font-medium hover:bg-primary/10"
                            data-testid="mobile-logout-button"
                        >
                            <LogOut className="h-4 w-4" />
                            {t("common.logout")}
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
